<script lang="ts">
  import { Icon, type IconName } from '$ui/primitives/Icon';

  let {
    icon = 'inbox',
    title,
    description,
    action,
    secondaryAction,
    variant = 'default',
    class: className = ''
  }: {
    icon?: IconName;
    title: string;
    description?: string;
    action?: { label: string; onclick: () => void };
    secondaryAction?: { label: string; onclick: () => void };
    variant?: 'default' | 'filter-empty' | 'success' | 'error' | 'lock';
    class?: string;
  } = $props();

  const role = $derived(variant === 'error' ? 'alert' : 'status');
</script>

<div class="empty {className}" data-variant={variant} {role}>
  <span class="icon" aria-hidden="true">
    <Icon name={icon} size={48} stroke={1.5} />
  </span>
  <h3 class="title">{title}</h3>
  {#if description}
    <p class="description">{description}</p>
  {/if}
  {#if action || secondaryAction}
    <div class="actions">
      {#if action}
        <button class="btn primary" type="button" onclick={action.onclick}>
          {action.label}
        </button>
      {/if}
      {#if secondaryAction}
        <button class="btn ghost" type="button" onclick={secondaryAction.onclick}>
          {secondaryAction.label}
        </button>
      {/if}
    </div>
  {/if}
</div>

<style>
  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--space-12) var(--space-4);
    max-width: 360px;
    margin: 0 auto;
    gap: var(--space-4);
  }

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-faint);
  }

  .empty[data-variant='error'] .icon {
    color: var(--color-danger);
  }

  .empty[data-variant='success'] .icon {
    color: var(--color-success);
  }

  .title {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.15rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.3;
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .description {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.6;
    color: var(--color-text-secondary);
    max-width: 44ch;
    text-wrap: pretty;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    justify-content: center;
    margin-top: var(--space-2);
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 var(--space-4);
    font-family: inherit;
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-radius: var(--radius-sm);
    border: var(--border-width) solid transparent;
    cursor: pointer;
    transition:
      background var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out),
      transform var(--dur-instant) var(--ease-out);
  }

  .btn:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  .btn:active {
    transform: scale(0.98);
  }

  .btn.primary {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .btn.primary:hover {
    background: var(--color-accent-hover);
  }

  .btn.ghost {
    background: transparent;
    color: var(--color-text-secondary);
    border-color: transparent;
  }

  .btn.ghost:hover {
    background: var(--color-surface-hover);
    color: var(--color-text-primary);
  }
</style>